import { FunctionComponent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Alert, ButtonLink } from '@backstage/ui';
// shortcut: generic MUI icon for SonarQube (same as the product team's tooling groups list), swap for a real logo once one exists.
import AssessmentIcon from '@material-ui/icons/Assessment';
import {
  isPluginEnabled,
  SectionCard,
} from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { SonarQubeServiceGroup } from '../../../api/service-groups/types';
import { ServiceGroupMembers } from './ServiceGroupMembers';
import { ServiceGroupGeneralInformation } from './ServiceGroupGeneralInformation';
import { ServiceGroupProjects } from './ServiceGroupProjects';
import { ServiceGroupActionsMenu } from './ServiceGroupActionsMenu';
import { DeleteServiceGroupDialog } from './DeleteServiceGroupDialog';
import styles from './SonarQubeServiceGroupPage.module.css';

interface SonarQubeServiceGroupPageProps {
  /** Called once the service group loads, so the app shell can show its name in the header. */
  onServiceGroupLoaded?: (serviceGroup: SonarQubeServiceGroup) => void;
  /** Entra ids of the product team's members; only they can be added. Undefined while loading. */
  teamMemberEntraIds?: string[];
}

/** Route: /product-teams/:id/tooling-groups/sonarqube/:serviceGroupId */
export const SonarQubeServiceGroupPage: FunctionComponent<
  SonarQubeServiceGroupPageProps
> = ({ onServiceGroupLoaded, teamMemberEntraIds }) => {
  const configApi = useApi(configApiRef);
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const navigate = useNavigate();
  const { id = '', serviceGroupId = '' } = useParams();
  const serviceGroup = useAsync(
    () => sonarqubeApi.getServiceGroup(serviceGroupId),
    [sonarqubeApi, serviceGroupId],
  );
  const membership = useAsync(
    () => sonarqubeApi.getMembership(serviceGroupId),
    [sonarqubeApi, serviceGroupId],
  );
  const isAdmin = membership.value?.role === 'admin';
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (serviceGroup.value) {
      onServiceGroupLoaded?.(serviceGroup.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceGroup.value]);

  const serviceGroupPath = `/product-teams/${encodeURIComponent(
    id,
  )}/tooling-groups/sonarqube/${encodeURIComponent(serviceGroupId)}`;

  if (!isPluginEnabled(configApi, 'sonarqube')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.body}>
      {isAdmin && (
        <div className={styles.header}>
          <ServiceGroupActionsMenu
            onUpdate={() => navigate(`${serviceGroupPath}/edit`)}
            onDelete={() => setDeleteOpen(true)}
          />
        </div>
      )}
      <DeleteServiceGroupDialog
        serviceGroupId={serviceGroupId}
        name={serviceGroup.value?.applicationName}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate(`/product-teams/${encodeURIComponent(id)}`)}
      />
      <div className={styles.grid}>
        <div className={styles.fullWidth}>
          <Alert
            status="info"
            description="Manage this tooling group from the Developer Platform. Changes made directly in SonarQube may not show up here and can cause it to get out of sync."
          />
        </div>
        <SectionCard
          title="General information"
          action={
            serviceGroup.value && (
              <ButtonLink
                variant="tertiary"
                iconStart={<AssessmentIcon />}
                href={serviceGroup.value.applicationUrl}
                target="_blank"
              >
                View in SonarQube
              </ButtonLink>
            )
          }
        >
          <ServiceGroupGeneralInformation state={serviceGroup} />
        </SectionCard>
        <ServiceGroupMembers
          serviceGroupId={serviceGroupId}
          isAdmin={isAdmin}
          teamMemberEntraIds={teamMemberEntraIds}
        />
        <div className={styles.fullWidth}>
          <ServiceGroupProjects
            serviceGroupId={serviceGroupId}
            onCreate={() => navigate(`${serviceGroupPath}/projects/create`)}
            onUpdate={projectKey =>
              navigate(
                `${serviceGroupPath}/projects/${encodeURIComponent(
                  projectKey,
                )}/edit`,
              )
            }
          />
        </div>
      </div>
    </div>
  );
};
